import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import EssayDetailView from './EssayDetailView';
import ShareDocButton from './ShareDocButton';

// The same essay detail screen a student sees, opened by a parent or an
// advisor. What comes back from the server is already filtered by their
// access, and what they may do here is filtered again by the same flags — a
// capability that is off is not merely hidden.
export default function SharedEssayDetailPage() {
  const { grantId, essayId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { grant: shellGrant } = useOutletContext();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const composerRef = useRef(null);

  const load = useCallback(
    () =>
      api
        .get(`/college-access/shared/${grantId}/essays/${essayId}`)
        .then((res) => setData(res.data))
        .catch((err) => {
          toast.error(err.response?.data?.error || 'Failed to open that essay');
          navigate(`/shared/${grantId}`);
        }),
    [api, grantId, essayId, navigate]
  );

  useEffect(() => {
    let alive = true;
    load().finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [load]);

  const grant = data?.grant || shellGrant;

  const viewer = useMemo(
    () => (grant ? { kind: 'grant', id: grant.id, name: grant.name, role: grant.role } : null),
    [grant]
  );

  // Say so when an email actually went out, and say so when it didn't.
  const announce = (delivery) => {
    if (delivery?.sent) toast.success(`Emailed ${delivery.to.length === 1 ? '1 person' : `${delivery.to.length} people`}`);
    else if (delivery?.error) toast.error("Saved, but the email didn't send");
  };

  const actions = useMemo(
    () => ({
      composerRef,
      focusComposer: () => composerRef.current?.focus(),
      postNote: async (body, parentId = null, { notify } = {}) => {
        const res = await api.post(`/college-access/shared/${grantId}/essays/${essayId}/notes`, {
          body,
          parentId,
          notify: notify === true,
        });
        setData((prev) => ({
          ...prev,
          essay: { ...prev.essay, brainstormNotes: [...prev.essay.brainstormNotes, res.data.note] },
        }));
        announce(res.data.delivery);
      },
      addTask: async (payload) => {
        const res = await api.post(`/college-access/shared/${grantId}/essays/${essayId}/tasks`, payload);
        setData((prev) => ({ ...prev, essay: { ...prev.essay, tasks: [...prev.essay.tasks, res.data.task] } }));
        if (!res.data.delivery?.sent) toast.success('Sent');
        announce(res.data.delivery);
      },
      attachDoc: async (payload) => {
        const res = await api.post(`/college-access/shared/${grantId}/essays/${essayId}/docs`, payload);
        setData((prev) => ({ ...prev, docs: [res.data.doc, ...prev.docs] }));
        announce(res.data.delivery);
        // Grants don't hold Drive credentials in the app, so we can't share on
        // their behalf. But we can tell them who ought to be able to open it,
        // so they can share it themselves in Google.
        try {
          const audienceRes = await api.get(`/college-access/shared/${grantId}/essays/${essayId}/draft-audience`);
          const audience = audienceRes.data.audience || [];
          if (audience.length > 0) {
            const names = audience.map((a) => a.name || a.email);
            const short = names.length <= 2 ? names.join(' and ') : `${names.slice(0, 2).join(', ')} and ${names.length - 2} other${names.length - 2 === 1 ? '' : 's'}`;
            toast.message(`Share the doc in Google with ${short} so they can open it`);
          }
        } catch {
          // Not fatal — the doc still attached.
        }
      },
      setTaskStatus: async (task, status) => {
        try {
          const res = await api.patch(`/college-access/shared/${grantId}/tasks/${task.id}`, { status });
          setData((prev) => ({
            ...prev,
            essay: {
              ...prev.essay,
              tasks: prev.essay.tasks.map((t) => (t.id === task.id ? { ...t, status: res.data.task.status } : t)),
            },
          }));
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to update that task');
        }
      },
    }),
    [api, grantId, essayId]
  );

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)' }}>
          Opening…
        </p>
      </div>
    );
  }
  if (!data) return null;

  const { essay, docs, people } = data;
  const school = essay.collegeApplication?.collegeName || essay.school;

  return (
    <EssayDetailView
      essay={essay}
      docs={docs}
      people={people}
      viewer={viewer}
      can={{
        viewerIsOwner: false,
        editDetails: false,
        changeStage: false,
        markReviewed: false,
        manageAccess: false,
        manageAnyTask: false,
        // The student decides which note is "the angle" and who does what on
        // their own essay; a grant can only ask, and only if allowed to.
        star: false,
        addTask: !!grant.assignTasks,
        assignOthers: false,
        comment: !!grant.comment,
        seeChecklist: false,
        attachDocs: false,
        readDrafts: essay.draftsVisible,
      }}
      actions={actions}
      docPickerSlot={
        grant.comment ? <ShareDocButton ownerName={grant.ownerName} onAttach={actions.attachDoc} /> : null
      }
      breadcrumb={[
        { label: `${grant.ownerName}'s desk`, to: `/shared/${grantId}` },
        ...(grant.seeProgress ? [{ label: 'Essays', to: `/shared/${grantId}/essays` }] : []),
        ...(school ? [{ label: school }] : []),
      ]}
    />
  );
}
